import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Accordion } from 'react-bootstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers/yup'

import { completCleanningSchema } from '../../../util/validationSchema'
import { errorToast, successToast } from '../../../util/Toast/toast'
import { SpinnerContext } from '../../../context/spinnerContext'
import { completePropertyScheduleRequest } from '../../../redux/slices/propertySlice'
import { getTimeStamp, isDateAfter } from '../../../helper/function'

import GetReportForm from './property/GetReportForm'
import GetCleaningForm from './property/GetCleaningForm'
import { getReportCategory } from '../../../redux/slices/reportSlice'
import CommonDateTimePicker from '../../commonDateTimePicker'

const CompleteCleaningForm = () => {
  const navigate = useNavigate()
  const params = useParams()
  const property_schedule_id = params?.property_schedule_id
  const dispatch = useDispatch()
  const { changeLoader } = useContext(SpinnerContext)
  const { reportCategory } = useSelector((state) => state.report)
  const [accrdionActiveKey, setAccrdionActiveKey] = useState(null)

  const defaluValues = { cleaning_images: [], reports: [{ cleaning_reports_category: null, title: '', description: '', images: [] }] }

  const methods = useForm({
    resolver: yupResolver(completCleanningSchema),
    mode: 'onBlur',
    defaultValues: defaluValues
  })

  const {
    handleSubmit,
    control,
    trigger,
    formState: { errors },
    register,
    watch,
    setValue
  } = methods

  useEffect(() => {
    if (property_schedule_id) {
      return
    }
    onHandleBack()
  }, [property_schedule_id])

  useEffect(() => {
    getCategory()
  }, [])

  const onHandleBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const onSubmit = (val) => {
    onHandleUpdate(val)
  }
  const onHandleUpdate = async (val) => {
    const newReport = val?.reports
      ?.map((item) => ({
        cleaning_reports_category: item?.cleaning_reports_category?.category,
        cleaning_reports_category_id: item?.cleaning_reports_category?.cleaning_reports_category_id,
        title: item?.title,
        images: item?.images,
        description: item?.description
      }))
      .filter((item) => item.cleaning_reports_category)
    //create payload
    let payload = {
      data: {
        ...(val?.started_cleaning_at && { started_cleaning_at: getTimeStamp(val?.started_cleaning_at) }),
        ...(val?.ended_cleaning_at && { ended_cleaning_at: getTimeStamp(val?.ended_cleaning_at) }),
        ...(val?.cleaning_images?.length !== 0 && { cleaning_images: val?.cleaning_images }),
        ...(newReport?.length !== 0 && { cleaning_reports: newReport })
      },
      property_id: property_schedule_id
    }
    changeLoader(true)
    await dispatch(completePropertyScheduleRequest(payload))
      .then(({ payload }) => {
        if (payload?.status === 200) {
          successToast(payload?.data?.status_message || 'Success!')
          onHandleBack()
          changeLoader(false)
        } else {
          errorToast(payload?.data?.status_message || 'Failed!')
          changeLoader(false)
        }
      })
      .catch((err) => {
        errorToast(err?.message || 'Failed!')
      })
  }

  const getCategory = () => {
    dispatch(getReportCategory())
  }

  useEffect(() => {
    const errorMapping = {
      cleaning: errors.cleaning_images,
      report: errors.reports
    }

    if (errorMapping[accrdionActiveKey]) return

    for (const [key, hasError] of Object.entries(errorMapping)) {
      if (hasError) {
        setAccrdionActiveKey(key)
        return
      }
    }
  }, [errors])

  const onAccordionChange = (key) => {
    setAccrdionActiveKey((prev) => (prev !== key ? key : null))
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="title_header">
          <h5 className="sec_title_text d-flex align-items-center">
            <i className="fa fa-angle-left fs-5 me-2" aria-hidden="true" onClick={() => onHandleBack()}></i>
            Add Cleaning Information
          </h5>
        </div>
        <div className={'card'}>
          <div className={'card-body card_body_div'}>
            <>
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>
                            Cleaning Start<span className="text-danger"> *</span>
                          </label>
                          <div className="date_picker_main_div">
                            <CommonDateTimePicker
                              name="started_cleaning_at"
                              control={control}
                              placeholder="yyyy-MM-ddTHH:mm A"
                              onDateChange={() => {
                                trigger('started_cleaning_at')
                              }}
                              shouldDisableDate={(date) => isDateAfter(date, watch('ended_cleaning_at'))}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>
                            Cleaning End<span className="text-danger"> *</span>
                          </label>
                          <div className="date_picker_main_div">
                            <CommonDateTimePicker
                              name="ended_cleaning_at"
                              control={control}
                              placeholder="yyyy-MM-ddTHH:mm A"
                              onDateChange={() => {
                                trigger('ended_cleaning_at')
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Accordion Design Start */}
                    <div className="accordion_main_div">
                      <Accordion activeKey={accrdionActiveKey}>
                        <Accordion.Item eventKey="cleaning">
                          <Accordion.Header onClick={() => onAccordionChange('cleaning')}>Cleaning</Accordion.Header>
                          <Accordion.Body>
                            <GetCleaningForm control={control} errors={errors} register={register} watch={watch} setValue={setValue} />
                          </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="report">
                          <Accordion.Header onClick={() => onAccordionChange('report')}>Report</Accordion.Header>
                          <Accordion.Body>
                            <GetReportForm
                              control={control}
                              errors={errors}
                              register={register}
                              watch={watch}
                              setValue={setValue}
                              category={reportCategory}
                            />
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>
                    </div>

                    <div className="multi_btn_div mt-2">
                      <button
                        type="button"
                        className="btn common-outline-btn w-100"
                        onClick={() => {
                          onHandleBack()
                        }}
                      >
                        Close
                      </button>
                      <button type="submit" className="btn btn-primary common-field-btn w-100">
                        Save
                      </button>
                    </div>
                  </>
                </form>
              </FormProvider>
            </>
          </div>
        </div>
      </div>
    </div>
  )
}
export default CompleteCleaningForm
